import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';

import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconTrash } from '@sorare/core/src/atoms/icons/system/iconTrash.ts';
import { useToggleSubscription } from '@sorare/core/src/hooks/subscriptions/useToggleSubscription';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useRemoveFromWatchlist } from '#src/hooks/watchlist/useRemoveFromWatchlist.ts';

import type { RemoveFromWatchlistButton_anyPlayer } from './__generated__/index.graphql.ts';

type Props = {
  anyPlayer: RemoveFromWatchlistButton_anyPlayer;
  watchlistId?: string;
  favoritesWatchlist?: boolean;
  onClose?: () => void;
};

export const RemoveFromWatchlistButton = ({
  anyPlayer,
  watchlistId,
  favoritesWatchlist,
  onClose,
}: Props) => {
  const { removeFromWatchlist } = useRemoveFromWatchlist();

  const { refresh } = useInstantSearch() || {};
  const toggleSubscription = useToggleSubscription(
    anyPlayer.currentUserSubscription,
    anyPlayer,
    undefined,
    [namedOperations.Query.FavoritesWatchlistPageQuery]
  );

  const onRemove = async () => {
    if (watchlistId) {
      await removeFromWatchlist.mutate(watchlistId, [
        anyPlayer.printablePlayer?.id,
      ]);
      return;
    }
    if (favoritesWatchlist) {
      toggleSubscription();
    }
  };

  return (
    <Button
      size="medium"
      fullWidth
      color="destructive"
      blur
      onClick={async () => {
        await onRemove();
        refresh?.();
        onClose?.();
      }}
      startIcon={<FontAwesomeIcon icon={iconTrash} />}
    >
      <FormattedMessage {...glossary.remove} />
    </Button>
  );
};

RemoveFromWatchlistButton.fragments = {
  anyPlayer: gql`
    fragment RemoveFromWatchlistButton_anyPlayer on AnyPlayerInterface {
      slug
      currentUserSubscription {
        slug
      }
      printablePlayer {
        id
      }
    }
  ` as TypedDocumentNode<RemoveFromWatchlistButton_anyPlayer>,
};
